import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private currentTheme = new BehaviorSubject<string>('green_theme');
  public currentTheme$ = this.currentTheme.asObservable();

  private readonly THEME_STORAGE_KEY = 'user-theme';
  private readonly API_URL = environment.apiUrl;

  private readonly availableThemes = [
    { value: 'blue-theme', label: 'Blue Theme', color: '#00a1ff' },
    { value: 'dark-theme', label: 'Dark Theme', color: '#1e293b' },
    { value: 'orange_theme', label: 'Orange Theme', color: '#ff6b35' },
    { value: 'aqua_theme', label: 'Aqua Theme', color: '#16cdc7' },
    // { value: 'purple_theme', label: 'Purple Theme', color: '#7460ee' },
    { value: 'green_theme', label: 'Green Theme', color: '#26c281' },
    { value: 'cyan_theme', label: 'Cyan Theme', color: '#00bcd4' },
  ];

  constructor(private http: HttpClient) {
    this.initializeTheme();
  }

  /**
   * Initialize theme from localStorage or use default
   */
  private initializeTheme(): void {
    const savedTheme = localStorage.getItem(this.THEME_STORAGE_KEY);
    if (savedTheme && this.isValidTheme(savedTheme)) {
      this.applyTheme(savedTheme);
    } else {
      this.applyTheme('green_theme');
    }
  }

  /**
   * Get current theme value
   */
  getCurrentTheme(): string {
    return this.currentTheme.value;
  }

  /**
   * Get available themes list
   */
  getAvailableThemes() {
    return this.availableThemes;
  }

  /**
   * Apply theme to the document
   */
  applyTheme(theme: string): void {
    if (!this.isValidTheme(theme)) {
      console.warn(`Invalid theme: ${theme}. Falling back to green_theme.`);
      theme = 'green_theme';
    }

    // Remove all theme classes
    const body = document.body;
    this.availableThemes.forEach(t => {
      body.classList.remove(t.value);
    });

    // Add the new theme class
    body.classList.add(theme);

    // Update BehaviorSubject
    this.currentTheme.next(theme);

    // Save to localStorage
    localStorage.setItem(this.THEME_STORAGE_KEY, theme);
  }

  /**
   * Set theme from user profile data
   */
  setThemeFromUser(theme: string | null | undefined): void {
    if (theme && this.isValidTheme(theme)) {
      this.applyTheme(theme);
    }
  }

  /**
   * Update user's theme preference on the server
   */
  updateUserTheme(userId: number, theme: string): Observable<any> {
    if (!this.isValidTheme(theme)) {
      throw new Error(`Invalid theme: ${theme}`);
    }

    return this.http.put(`${this.API_URL}/users/theme/${userId}`, {
      preferred_theme: theme
    }).pipe(
      tap(() => {
        this.applyTheme(theme);
      })
    );
  }

  /**
   * Validate if theme exists in available themes
   */
  private isValidTheme(theme: string): boolean {
    return this.availableThemes.some(t => t.value === theme);
  }
}
